// frontend/src/contexts/ProgressiveResultsContext.jsx
// Context for managing progressive scoping results and real-time updates

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  triggerScopingWithProgress,
  getScopingProgress,
  getActiveSessions,
} from "../services/progressiveResults.js";

const ProgressiveResultsContext = createContext();

export const useProgressiveResults = () => {
  const context = useContext(ProgressiveResultsContext);
  if (!context) {
    throw new Error(
      "useProgressiveResults must be used within a ProgressiveResultsProvider"
    );
  }
  return context;
};

export const ProgressiveResultsProvider = ({ children }) => {
  // State management
  const [activeSessions, setActiveSessions] = useState({});
  const [sessionProgress, setSessionProgress] = useState({});
  const [pollingIntervals, setPollingIntervals] = useState({});
  const [isPolling, setIsPolling] = useState(false);

  // Refs for cleanup
  const pollingRefs = useRef({});
  const cleanupTimeoutRef = useRef(null);

  // Polling configuration
  const ACTIVE_POLLING_INTERVAL = 5000; // 5 seconds for active sessions (reduced from 1s)
  const INACTIVE_POLLING_INTERVAL = 10000; // 10 seconds for completed sessions
  const SESSION_CLEANUP_DELAY = 300000; // 5 minutes after completion

  /**
   * Start a new scoping session with progressive results
   */
  const startScopingSession = useCallback(async (options = {}) => {
    console.log(
      `[ProgressiveResults] startScopingSession called with options:`,
      options
    );
    try {
      const result = await triggerScopingWithProgress(options);

      if (result.success && result.session_id) {
        const sessionId = result.session_id;
        console.log(`[ProgressiveResults] Session created: ${sessionId}`);

        // Initialize session data
        setActiveSessions((prev) => ({
          ...prev,
          [sessionId]: {
            sessionId,
            status: "pending",
            startedAt: new Date().toISOString(),
            options,
            result,
          },
        }));

        // Start polling for this session
        console.log(
          `[ProgressiveResults] Starting polling for session ${sessionId}`
        );
        startPolling(sessionId);

        return {
          success: true,
          sessionId,
          message: result.message,
          options: result,
        };
      } else {
        throw new Error(result.error || "Failed to start scoping session");
      }
    } catch (error) {
      console.error("Error starting scoping session:", error);
      throw error;
    }
  }, []);

  /**
   * Start polling for a specific session
   */
  const startPolling = useCallback(
    (sessionId) => {
      console.log(
        `[ProgressiveResults] Starting polling for session ${sessionId}`
      );

      // Guard: prevent multiple polling intervals for the same session
      if (pollingRefs.current[sessionId]) {
        console.log(
          `[ProgressiveResults] Polling already active for session ${sessionId}, skipping`
        );
        return;
      }

      // Start new polling with proper error handling
      const intervalId = setInterval(async () => {
        try {
          // Reduced logging - only log every 10th poll to reduce spam
          if (Math.random() < 0.1) {
            console.log(
              `[ProgressiveResults] Polling session ${sessionId} at ${new Date().toISOString()}`
            );
          }
          const progressData = await getScopingProgress(sessionId);

          if (progressData.success && progressData.progress) {
            const progress = progressData.progress;

            // Update session progress
            setSessionProgress((prev) => ({
              ...prev,
              [sessionId]: progress,
            }));

            // Update active session status
            setActiveSessions((prev) => ({
              ...prev,
              [sessionId]: {
                ...prev[sessionId],
                status: progress.status,
                updatedAt: new Date().toISOString(),
              },
            }));

            // Check if session is completed
            if (
              ["completed", "failed", "timeout", "cancelled"].includes(
                progress.status
              )
            ) {
              stopPolling(sessionId);

              // Schedule cleanup
              cleanupTimeoutRef.current = setTimeout(() => {
                cleanupSession(sessionId);
              }, SESSION_CLEANUP_DELAY);
            }
          }
        } catch (error) {
          console.error(`Error polling session ${sessionId}:`, error);

          // If session not found, stop polling
          if (error.message.includes("not found")) {
            stopPolling(sessionId);
          }
        }
      }, ACTIVE_POLLING_INTERVAL);

      // Store interval reference
      pollingRefs.current[sessionId] = intervalId;
      console.log(
        `[ProgressiveResults] Polling started for session ${sessionId} with interval ${ACTIVE_POLLING_INTERVAL}ms`
      );

      // Update polling state
      setPollingIntervals((prev) => ({
        ...prev,
        [sessionId]: intervalId,
      }));
    },
    [ACTIVE_POLLING_INTERVAL, SESSION_CLEANUP_DELAY]
  );

  /**
   * Stop polling for a specific session
   */
  const stopPolling = useCallback((sessionId) => {
    if (pollingRefs.current[sessionId]) {
      clearInterval(pollingRefs.current[sessionId]);
      delete pollingRefs.current[sessionId];
    }

    setPollingIntervals((prev) => {
      const newIntervals = { ...prev };
      delete newIntervals[sessionId];
      return newIntervals;
    });

    // Update polling state
    const remainingIntervals = Object.keys(pollingRefs.current).length;
    setIsPolling(remainingIntervals > 0);
  }, []);

  /**
   * Clean up a completed session
   */
  const cleanupSession = useCallback(
    (sessionId) => {
      setActiveSessions((prev) => {
        const newSessions = { ...prev };
        delete newSessions[sessionId];
        return newSessions;
      });

      setSessionProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[sessionId];
        return newProgress;
      });

      stopPolling(sessionId);
    },
    [stopPolling]
  );

  /**
   * Get progress for a specific session
   */
  const getSessionProgress = useCallback(
    (sessionId) => {
      return sessionProgress[sessionId] || null;
    },
    [sessionProgress]
  );

  /**
   * Get all active sessions
   */
  const getAllActiveSessions = useCallback(() => {
    return Object.values(activeSessions);
  }, [activeSessions]);

  /**
   * Get sessions by status
   */
  const getSessionsByStatus = useCallback(
    (status) => {
      return Object.values(activeSessions).filter(
        (session) => session.status === status
      );
    },
    [activeSessions]
  );

  /**
   * Force refresh progress for a session
   */
  const refreshSessionProgress = useCallback(async (sessionId) => {
    try {
      const progressData = await getScopingProgress(sessionId);

      if (progressData.success && progressData.progress) {
        setSessionProgress((prev) => ({
          ...prev,
          [sessionId]: progressData.progress,
        }));
        return progressData.progress;
      }
    } catch (error) {
      console.error(`Error refreshing session ${sessionId}:`, error);
      throw error;
    }
  }, []);

  /**
   * Manually load active sessions (for when backend becomes available)
   */
  const loadActiveSessions = useCallback(async () => {
    try {
      // Reduced logging - only log occasionally
      if (Math.random() < 0.05) {
        console.log("[ProgressiveResultsContext] Loading active sessions...");
      }
      const result = await getActiveSessions();

      if (result.success && result.active_sessions) {
        // Only log when there are sessions to process
        if (result.active_sessions.length > 0 && Math.random() < 0.1) {
          console.log(
            `[ProgressiveResultsContext] Processing ${result.active_sessions.length} sessions`
          );
        }
        const sessions = {};
        result.active_sessions.forEach((session) => {
          sessions[session.session_id] = {
            sessionId: session.session_id,
            status: session.status,
            startedAt: session.started_at,
            updatedAt: session.updated_at,
            options: {},
            // Store the full progress data from backend
            progress: {
              session_id: session.session_id,
              status: session.status,
              started_at: session.started_at,
              updated_at: session.updated_at,
              total_runtime: session.total_runtime,
              progress_percentage: session.progress_percentage,
              total_collections: session.total_collections,
              completed_collections: session.completed_collections,
              failed_collections: session.failed_collections,
              scoping_collections: session.scoping_collections,
              cached_collections: session.cached_collections,
              current_collection: session.current_collection,
              estimated_remaining_time: session.estimated_remaining_time,
              error_message: session.error_message,
              collections: session.collections,
            },
          };
        });
        console.log(
          "[ProgressiveResultsContext] Processed sessions:",
          sessions
        );

        // Merge backend data with existing local state to preserve local changes
        setActiveSessions((prevSessions) => {
          const mergedSessions = { ...prevSessions };

          // Update with backend data
          Object.keys(sessions).forEach((sessionId) => {
            mergedSessions[sessionId] = sessions[sessionId];
          });

          return mergedSessions;
        });
        return sessions;
      }
    } catch (error) {
      console.warn(
        "Backend not available for loading active sessions:",
        error.message
      );
      throw error;
    }
  }, []);

  /**
   * Stop all polling and cleanup
   */
  const stopAllPolling = useCallback(() => {
    Object.values(pollingRefs.current).forEach((intervalId) => {
      clearInterval(intervalId);
    });
    pollingRefs.current = {};
    setPollingIntervals({});
    setIsPolling(false);
  }, []);

  /**
   * Load existing active sessions on mount (disabled to avoid CORS errors)
   * Sessions will be loaded when explicitly requested by components
   */
  // useEffect(() => {
  //   const loadActiveSessions = async () => {
  //     try {
  //       const result = await getActiveSessions();
  //       if (result.success && result.active_sessions) {
  //         const sessions = {};
  //         result.active_sessions.forEach((session) => {
  //           sessions[session.session_id] = {
  //             sessionId: session.session_id,
  //             status: session.status,
  //             startedAt: session.started_at,
  //             updatedAt: session.updated_at,
  //             options: {},
  //           };
  //         });
  //         setActiveSessions(sessions);
  //       }
  //     } catch (error) {
  //       // Silently fail if backend is not available
  //       console.warn(
  //         "Backend not available for loading active sessions:",
  //         error.message
  //       );
  //     }
  //   };

  //   loadActiveSessions();
  // }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopAllPolling();
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [stopAllPolling]);

  const value = {
    // State
    activeSessions,
    sessionProgress,
    isPolling,

    // Actions
    startScopingSession,
    getSessionProgress,
    getAllActiveSessions,
    getSessionsByStatus,
    refreshSessionProgress,
    loadActiveSessions,
    stopPolling,
    cleanupSession,
    stopAllPolling,

    // Utilities
    SESSION_CLEANUP_DELAY,
  };

  return (
    <ProgressiveResultsContext.Provider value={value}>
      {children}
    </ProgressiveResultsContext.Provider>
  );
};
